import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { CONSTANT } from '@const';
import { UserTypeContext } from '@context';
import { NavLink, Separator } from '@general';
import { msalService } from '@services';
import { WaterIQUserType } from '@types';
import { useLogout } from '@utils';

import styles from './dropdownMenu.module.scss';

interface DropdownMenuProps {
	children: React.ReactNode;
	isOpen: boolean;
	closeMenu: () => void;
	isForMobile?: boolean;
}

export const DropdownMenu = ({
	children,
	isOpen,
	isForMobile = false,
	closeMenu,
}: DropdownMenuProps) => {
	// Context
	const { selectedUserType, setSelectedUserType } = useContext(UserTypeContext);
	// Memo
	const isValidNonDQPUser = useMemo(
		() => msalService.isValidAccount() && selectedUserType != WaterIQUserType.DQP,
		[selectedUserType],
	);
	const availableUserTypes = useMemo(() => msalService.getUserTypes(), []);
	// Others
	const logout = useLogout();
	const dropdownRef = useRef<HTMLDivElement>(null);

	// event listener to handle click outsise
	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			const target = event.target as Node;
			const isClickEventOutside = dropdownRef?.current && !dropdownRef?.current?.contains(target);
			if (isClickEventOutside) {
				closeMenu();
			}
		};

		document.addEventListener('click', handleClickOutside);

		return () => document.removeEventListener('click', handleClickOutside);
	}, [closeMenu]);

	const selectUserTypeOption = (userType: WaterIQUserType) => {
		setSelectedUserType(userType);
		closeMenu();
	};

	return (
		<div className={styles['dropdown']} ref={dropdownRef}>
			{children}
			<div
				className={[styles['dropdown-menu'], !isOpen && styles['dropdown-menu-hidden']].join(' ')}
			>
				{(availableUserTypes ?? []).length > 1 && (
					<div className={styles['dropdown-menu-user-types']}>
						{availableUserTypes?.map((userType, index) => (
							<p
								onClick={() => selectUserTypeOption(userType)}
								key={index}
								className={selectedUserType == userType ? styles.selected : ''}
							>
								WaterIQ {userType}
							</p>
						))}
						<Separator />
					</div>
				)}
				{!isForMobile && (
					<>
						<NavLink href={'/account'}>
							<p onClick={closeMenu}>Account details</p>
						</NavLink>
						<p
							onClick={() => {
								closeMenu();
								logout();
							}}
						>
							Log out
						</p>
					</>
				)}
				{isForMobile && (
					<>
						{msalService.isValidUnlinkedAccount() && (
							<NavLink href={'/registration'}>
								<p onClick={closeMenu}>Registration</p>
							</NavLink>
						)}
						<NavLink href={'/account'}>
							<p onClick={closeMenu}>Account details</p>
						</NavLink>
						{isValidNonDQPUser && (
							<NavLink href={'/deviceReadingSubmission'}>
								<p onClick={closeMenu}>Submit readings</p>
							</NavLink>
						)}
						{isValidNonDQPUser && (
							<NavLink href={'/waterAccounts'}>
								<p onClick={closeMenu}>Water Accounts</p>
							</NavLink>
						)}
						{isValidNonDQPUser && (
							<NavLink href={'/announcements'}>
								<p onClick={closeMenu}>Announcement dashboard</p>
							</NavLink>
						)}
						<NavLink href={'/contact'}>
							<p onClick={closeMenu}>Contact us</p>
						</NavLink>

						<NavLink
							href={CONSTANT.TERMS_AND_CONDITIONS_URL as string}
							isExternal
							onClick={() => {
								closeMenu();
							}}
						>
							<p>Terms and Conditions</p>
						</NavLink>
						<NavLink
							href={CONSTANT.HELP_URL as string}
							isExternal
							onClick={() => {
								closeMenu();
							}}
						>
							<p>Help</p>
						</NavLink>
						{CONSTANT.SHOW_DEV_PAGES && (
							<NavLink href={'/mvp/playground'}>
								<p onClick={closeMenu}>Playground</p>
							</NavLink>
						)}
						<p
							onClick={() => {
								closeMenu();
								logout();
							}}
						>
							Log out
						</p>
					</>
				)}
			</div>
		</div>
	);
};
